import { useEffect, useRef, useState } from "react";
import { Music, VolumeX, Loader2 } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";

// Local audio file served from /public/audio. Replace anytime with your own MP3.
const MUSIC_SRC = "/audio/wedding-ambient.mp3";

export default function MusicPlayer() {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [everPlayed, setEverPlayed] = useState(false);

  useEffect(() => {
    const a = audioRef.current;
    if (a) a.volume = 0.7;

    // Show the first-time hint 2.2s after load; hide after 7s or first interaction
    const appear = setTimeout(() => {
      const dismissed = localStorage.getItem("iw_music_hint_dismissed");
      if (!dismissed) setShowHint(true);
    }, 2200);
    const vanish = setTimeout(() => setShowHint(false), 9200);
    return () => {
      clearTimeout(appear);
      clearTimeout(vanish);
    };
  }, []);

  const dismissHint = () => {
    setShowHint(false);
    localStorage.setItem("iw_music_hint_dismissed", "1");
  };

  const toggle = async () => {
    dismissHint();
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      setPlaying(false);
      return;
    }
    try {
      setLoading(true);
      if (a.readyState < 2) {
        a.load();
      }
      await a.play();
      setPlaying(true);
      setEverPlayed(true);
    } catch (e) {
      console.error("Music play failed:", e?.message || e);
      toast.error(
        "Music could not play. Please tap the button again or check your device volume."
      );
      setPlaying(false);
    } finally {
      setLoading(false);
    }
  };

  // Attention-seeking pulse only until first successful play
  const attentionRing = !everPlayed && !loading;

  return (
    <div
      className="fixed bottom-5 left-5 md:bottom-8 md:left-8 z-40"
      data-testid="music-player"
    >
      <audio ref={audioRef} src={MUSIC_SRC} loop preload="auto" />

      {/* First-time guided hint */}
      <AnimatePresence>
        {showHint && !everPlayed && (
          <motion.div
            initial={{ opacity: 0, x: -14, scale: 0.94 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: -8, scale: 0.96 }}
            transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
            className="absolute bottom-0 left-[72px] md:left-[76px] whitespace-nowrap"
            data-testid="music-hint"
          >
            <div
              className="relative px-4 py-2.5 pr-8 flex items-center gap-2.5"
              style={{
                background:
                  "linear-gradient(135deg, rgba(26,20,20,0.95) 0%, rgba(42,26,30,0.95) 100%)",
                border: "1px solid rgba(212,175,55,0.55)",
                backdropFilter: "blur(12px)",
                boxShadow: "0 10px 40px -10px rgba(232,165,180,0.45)",
              }}
            >
              {/* Left arrow notch */}
              <span
                aria-hidden="true"
                className="absolute top-1/2 -left-[6px] -translate-y-1/2 w-3 h-3 rotate-45"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(26,20,20,0.95) 0%, rgba(42,26,30,0.95) 100%)",
                  borderLeft: "1px solid rgba(212,175,55,0.55)",
                  borderBottom: "1px solid rgba(212,175,55,0.55)",
                }}
              />
              <span
                className="italic text-[var(--iw-ivory)]"
                style={{
                  fontFamily: "Cormorant Garamond, serif",
                  fontSize: "0.95rem",
                  lineHeight: 1.1,
                }}
              >
                Tap for a touch of{" "}
                <span className="gold-rose-gradient not-italic font-medium">
                  shehnai
                </span>
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  dismissHint();
                }}
                aria-label="Dismiss music hint"
                data-testid="music-hint-dismiss"
                className="absolute top-1 right-1 w-5 h-5 grid place-items-center text-[var(--iw-faint)] hover:text-[var(--iw-gold)] text-xs"
              >
                ×
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <button
        onClick={toggle}
        aria-label={playing ? "Pause music" : "Play music"}
        data-testid="music-toggle"
        className="group relative w-14 h-14 rounded-full grid place-items-center transition-transform duration-300 hover:scale-110"
        style={{
          background:
            "linear-gradient(135deg, rgba(10,10,10,0.9) 0%, rgba(30,18,22,0.9) 100%)",
          border: "1px solid rgba(212,175,55,0.5)",
          backdropFilter: "blur(14px)",
          boxShadow: "0 10px 40px -10px rgba(232,165,180,0.5)",
        }}
      >
        {/* Slow attention pulse — only until first play */}
        {attentionRing && (
          <>
            <span
              aria-hidden="true"
              className="absolute inset-0 rounded-full animate-ping"
              style={{
                border: "1px solid rgba(212,175,55,0.55)",
                animationDuration: "2.6s",
              }}
            />
            <span
              aria-hidden="true"
              className="absolute -inset-1 rounded-full animate-ping"
              style={{
                border: "1px solid rgba(232,165,180,0.4)",
                animationDuration: "2.6s",
                animationDelay: "0.8s",
              }}
            />
          </>
        )}
        {playing && (
          <span
            aria-hidden="true"
            className="absolute inset-0 rounded-full animate-ping"
            style={{
              border: "1px solid rgba(212,175,55,0.5)",
              animationDuration: "2.5s",
            }}
          />
        )}
        {loading ? (
          <Loader2 size={18} className="text-[var(--iw-gold)] animate-spin" />
        ) : playing ? (
          <VolumeX size={18} className="text-[var(--iw-gold)]" />
        ) : (
          <Music size={18} className="text-[var(--iw-gold)]" />
        )}
      </button>
      <div
        className="absolute -top-7 left-1/2 -translate-x-1/2 whitespace-nowrap overline pointer-events-none"
        style={{ color: "var(--iw-faint)", fontSize: "0.6rem" }}
      >
        {playing ? "now playing" : loading ? "loading…" : "play music"}
      </div>
    </div>
  );
}
